import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Heart, Sparkles, Users } from 'lucide-react';
import Header from '../components/Header';

const About = ({ cartItems, onRemoveFromCart, onUpdateQuantity }) => {
  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header
        cartItems={cartItems}
        onRemoveFromCart={onRemoveFromCart}
        onUpdateQuantity={onUpdateQuantity}
      />

      {/* Hero Section */}
      <section className="pt-40 md:pt-44 pb-12 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-charcoal-ink mb-6">
            About The Favor
          </h1>
          <p className="text-xl text-stone-taupe">
            Every moment, big or small, deserves to be celebrated
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-lg p-8 md:p-12 border border-stone-taupe/20">
            <div className="prose prose-lg max-w-none">
              <p className="text-charcoal-ink/90 leading-relaxed mb-6">
                The Favor was created from a love of design and a belief that every moment, big or small, deserves to be celebrated.
              </p>
              
              <p className="text-charcoal-ink/90 leading-relaxed mb-6">
                I'm a graphic designer with a knack for turning meaningful moments into thoughtfully designed details. Whether it's a birthday party, wedding, baby shower, or seasonal gathering, my goal is always the same: to help bring your vision to life in a way that feels personal, polished, and memorable.
              </p>
              
              <p className="text-charcoal-ink/90 leading-relaxed mb-6">
                I understand that not everyone has the time, tools, or creative confidence to design every detail themselves, and that's where The Favor comes in. I work <em>with</em> you, not just <em>for</em> you, to create custom party decor, favors, paper goods, and digital designs that reflect your style and tell your story.
              </p>
              
              <p className="text-charcoal-ink/90 leading-relaxed">
                From simple celebrations to once-in-a-lifetime milestones, these moments become the memories we carry with us. The Favor exists to help you honor them with intention, creativity, and care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-6 bg-soft-linen">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal-ink text-center mb-12">
            What Drives The Favor
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 border border-stone-taupe/20 text-center">
              <div className="w-16 h-16 rounded-full bg-charcoal-ink flex items-center justify-center mx-auto mb-6">
                <Heart className="w-8 h-8 text-warm-ivory" />
              </div>
              <h3 className="text-xl font-semibold text-charcoal-ink mb-3">
                Personal Connection
              </h3>
              <p className="text-charcoal-ink/80 leading-relaxed">
                Every design is crafted with care and attention to your unique story and style.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 border border-stone-taupe/20 text-center">
              <div className="w-16 h-16 rounded-full bg-charcoal-ink flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-8 h-8 text-warm-ivory" />
              </div>
              <h3 className="text-xl font-semibold text-charcoal-ink mb-3">
                Creative Excellence
              </h3>
              <p className="text-charcoal-ink/80 leading-relaxed">
                Thoughtful design that transforms your vision into beautiful, cohesive details.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-8 border border-stone-taupe/20 text-center">
              <div className="w-16 h-16 rounded-full bg-charcoal-ink flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-warm-ivory" />
              </div>
              <h3 className="text-xl font-semibold text-charcoal-ink mb-3">
                Collaborative Process
              </h3>
              <p className="text-charcoal-ink/80 leading-relaxed">
                Working together to bring your celebration to life exactly as you imagine it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-charcoal-ink mb-4">
            Let's create something special together
          </h2>
          <p className="text-xl text-stone-taupe mb-8">
            Browse our curated bundles or tell us about your custom design needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button
                size="lg"
                className="bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory text-lg px-8 py-6"
              >
                Shop Bundles
              </Button>
            </Link>
            <Link to="/?action=custom">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-charcoal-ink text-charcoal-ink hover:bg-soft-linen text-lg px-8 py-6"
              >
                Submit Custom Request
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal-ink text-warm-ivory py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_partyfavor-design/artifacts/kg186rqs_Photoroom_20260106_152029.png" 
                alt="The Favor" 
                className="h-20 md:h-24 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-warm-ivory/80">
                Custom party details, designed to be remembered.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Shop</h4>
              <ul className="space-y-2">
                <li><Link to="/#bundles" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Party Bundles</Link></li>
                <li><Link to="/#occasions" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Occasions</Link></li>
                <li><Link to="/?action=custom" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Custom Requests</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Information</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">About</Link></li>
                <li><Link to="/faq" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">FAQ</Link></li>
                <li><Link to="/turnaround-time" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Turnaround Times</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://www.instagram.com/thefavor.design/" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="text-warm-ivory/80 hover:text-warm-ivory transition-colors">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-warm-ivory/20 pt-8 text-center text-warm-ivory/60">
            <p>&copy; 2025 The Favor. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;